// src/pages/RolesAccess.jsx
// Control Panel → Roles & Access (Super Admins). For each group: its roles
// (Admin, Manager, User and any custom roles) and what each can do. Every
// change saves straight away and reaches everyone within about a minute.
// Super Admins always have every permission and aren't listed here.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useNotify } from '../components/Notifications';
import { fetchRoles, updateRole, createRole, deleteRole, resetRole, copyRoles } from '../utils/api';
import Loader from '../components/Loader';
import Emo from '../components/Emo';
import { usePhone } from '../hooks/useWindowWidth';

// Role levels, lowest to highest (for rows fixed by level)
const LEVEL_RANK = { user: 1, manager: 2, admin: 3, master_admin: 4, super_admin: 5 };   // 3.68.0: Master Admin

// Short names for "Works with" and the also-ticked message (3.21.2)
const SHORT = {
  'tab.outgoing': 'Outbox tab', 'tab.incoming': 'Inbox tab', 'inbox.markNew': 'Mark faxes new/seen', 'inbox.seenBy': 'See who opened faxes', 'tab.usage': 'Usage tab', 'tab.logs': 'Logs tab', 'logs.failedSent': 'Failed Sent', 'logs.failedReceived': 'Failed Received', 'logs.repeatedFailures': 'Repeated failures', 'logs.blockedAccess': 'Blocked access', 'logs.problemNumbers': 'Problem numbers', 'problemNumbers.mark': 'Mark problem numbers',
  'fax.viewPurged': 'Trash', 'tab.contacts': 'Contacts tab', 'tab.mySessions': 'Sessions (profile menu)', 'tab.settings': 'Settings tab', 'popups.delivered': 'Delivered pop-ups', 'popups.failed': 'Failed pop-ups', 'popups.received': 'Received pop-ups', 'popups.incomingFailed': 'Incoming-failed pop-ups', 'settings.retries': 'Fax Retries', 'settings.incomingEmails': 'Incoming Fax Emails', 'settings.lineNames': 'Fax Lines', 'settings.coverSheet': 'Cover Sheet', 'settings.defaultLine': 'Default Line', 'settings.blockedNumbers': 'Blocked Numbers', 'settings.notices': 'Notification Emails', 'contacts.edit': 'Name contacts', 'contacts.remove': 'Remove contacts', 'reminders.view': 'Reminders lists', 'reminders.send': 'Send reminders',
  'reminders.schedule': 'Change reminder schedule', 'users.view': 'People', 'usage.details': 'Usage details',
  'fax.resend': 'Resend', 'fax.priority': 'Mark priority ★', 'fax.cancelRetry': 'Cancel retries', 'fax.delete': 'Delete faxes', 'fax.files': 'Fax files',
  'fax.reports': 'Excel reports', 'users.reset2fa': 'Reset two-factor', 'users.add': 'Add people', 'users.remove': 'Remove access', 'users.unblock': 'Unblock people',
  'users.changeRole': 'Change roles', 'users.password': 'Reset passwords', 'users.online': "Who's online", 'users.export': 'People report', 'compliance.report': 'Compliance report',
};
const nameOf = k => SHORT[k] || k;

export default function RolesAccess() {
  // 3.51.2: phones: a narrower permission column (it stays on the left while the roles scroll)
  const phone = usePhone();
  const permCol = phone ? { ...S.permCol, minWidth: 150, maxWidth: 160, fontSize: 12.5 } : S.permCol;
  const roleTh = phone ? { ...S.th, minWidth: 96, padding: '10px 8px' } : S.th;
  const { getToken } = useAuth();
  const { notify, confirm } = useNotify();
  const [data, setData]       = useState(null);
  const [error, setError]     = useState('');
  const [groupId, setGroupId] = useState('');
  const [saving, setSaving]   = useState('');
  const [adding, setAdding]   = useState(false);
  const [renaming, setRenaming] = useState(null);   // { id, value }

  const load = useCallback(async () => {
    setError('');
    try {
      const r = await fetchRoles(getToken);
      setData(r);
      setGroupId(g => g || r.groups[0]?.id || '');
    } catch (e) { setError(e.message); }
  }, [getToken]);
  useEffect(() => { load(); }, [load]);

  const group = data?.groups.find(g => g.id === groupId);
  // Individual clients (3.19.12): "Users" and "People" read "Staff", and
  // there's no Teams row (they have no teams)
  const staff = group?.type === 'individual';
  // Individual clients have only an owner (Admin) and one staff member
  // (User), so only those two roles show and no roles can be added (3.19.14)
  const shownRoles = (group?.roles || []).filter(r => !staff || r.id === 'admin' || r.id === 'user');
  const sections = useMemo(() => {
    const word = t => (staff ? String(t).replace(/People \(Users tab\)/g, 'Staff').replace(/\bUsers tab\b/g, 'Staff tab').replace(/^Users$/, 'Staff').replace(/\bPeople\b/g, 'Staff') : t);
    const out = [];
    for (const p of data?.permissions || []) {
      if (staff && p.key === 'teams.manage') continue;
      const name = word(p.section);
      let sec = out.find(s => s.name === name);
      if (!sec) out.push(sec = { name, items: [] });
      sec.items.push({ ...p, label: word(p.label) });
    }
    return out;
  }, [data, staff]);

  async function setPermission(role, key, value) {
    setSaving(`${role.id}:${key}`);
    // Show it straight away; reload on failure
    setData(d => ({ ...d, groups: d.groups.map(g => g.id !== groupId ? g : {
      ...g, roles: g.roles.map(r => r.id !== role.id ? r : { ...r, permissions: { ...r.permissions, [key]: value } }),
    }) }));
    try {
      // 3.21.2: the server also ticks what this needs, or unticks what
      // needed this; show those too, and say so
      const r = await updateRole(groupId, role.id, { permissions: { [key]: value } }, getToken);
      const applied = r?.permissions || {};
      const extra = Object.keys(applied).filter(k => k !== key);
      if (extra.length) {
        setData(d => ({ ...d, groups: d.groups.map(g => g.id !== groupId ? g : {
          ...g, roles: g.roles.map(x => x.id !== role.id ? x : { ...x, permissions: { ...x.permissions, ...applied } }),
        }) }));
        const on = extra.filter(k => applied[k] === true).map(nameOf), off = extra.filter(k => applied[k] === false).map(nameOf);
        notify({ tone: 'info', title: `${role.label}: ${on.length ? `also ticked ${on.join(', ')}` : ''}${on.length && off.length ? '; ' : ''}${off.length ? `also unticked ${off.join(', ')}` : ''}`,
          detail: value ? 'It only works together with that.' : "They don't work without it." });
      }
    } catch (e) {
      notify({ tone: 'error', title: "Couldn't save the change", detail: e.message });
      await load();
    }
    setSaving('');
  }

  async function saveRename(e) {
    e.preventDefault();
    const role  = group.roles.find(r => r.id === renaming.id);
    const label = renaming.value.trim();
    if (!label || label === role.label) { setRenaming(null); return; }
    try {
      await updateRole(groupId, role.id, { label }, getToken);
      notify({ tone: 'success', title: 'Role renamed' });
      setRenaming(null);
      await load();
    } catch (e2) { notify({ tone: 'error', title: "Couldn't rename the role", detail: e2.message }); }
  }

  async function remove(role) {
    if (!(await confirm({ title: `Delete the role "${role.label}"?`, message: 'This can\'t be undone. Only roles nobody has can be deleted.', confirmLabel: 'Delete role', danger: true }))) return;
    try {
      await deleteRole(groupId, role.id, getToken);
      notify({ tone: 'success', title: 'Role deleted' });
      await load();
    } catch (e) { notify({ tone: 'error', title: "Couldn't delete the role", detail: e.message }); }
  }

  async function copyFrom(fromId) {
    if (!fromId) return;
    const from = data.groups.find(g => g.id === fromId);
    if (!(await confirm({ title: `Copy ${from.name}'s role settings to ${group.name}?`, message: `Every role and switch in ${group.name} is replaced with ${from.name}'s, including custom roles. People keep their roles.`, confirmLabel: 'Copy settings', danger: true }))) return;
    try {
      await copyRoles(groupId, fromId, getToken);
      notify({ tone: 'success', title: `${group.name} now uses ${from.name}'s role settings` });
      await load();
    } catch (e) { notify({ tone: 'error', title: "Couldn't copy the settings", detail: e.message }); }
  }

  async function reset(role) {
    if (!(await confirm({ title: `Put ${role.label} back to its defaults?`, message: 'Every switch for this role at this client goes back to what a new client of this kind starts with.', confirmLabel: 'Reset' }))) return;
    try {
      await resetRole(groupId, role.id, getToken);
      notify({ tone: 'success', title: `${role.label} is back to its defaults` });
      await load();
    } catch (e) { notify({ tone: 'error', title: "Couldn't reset the role", detail: e.message }); }
  }

  if (error) return <div role="alert" style={S.error}>{error}</div>;
  if (!data) return <Loader label="Loading roles…" />;

  return (
    <div>
      <div style={S.top}>
        <div>
          <h3 style={S.title}>Roles & Access</h3>
          <p style={S.sub}>
            What each role can see and do, set separately for each client. Changes save straight away and apply within a minute.
            Super Admins always have every permission.
          </p>
        </div>
        {!staff && <button onClick={() => setAdding(a => !a)} style={S.primaryBtn}>{adding ? 'Close' : '+ Add role'}</button>}
      </div>

      <div style={S.groupRow}>
        <label style={S.groupLabel}>Client
          <select value={groupId} onChange={e => setGroupId(e.target.value)} style={S.select}>
            {data.groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
          </select>
        </label>
        {group && <span style={S.domains}>Email domains: {group.domains.join(', ') || '—'}</span>}
        {group && data.groups.length > 1 && (
          <label style={S.groupLabel}>Copy settings from
            <select value="" onChange={e => copyFrom(e.target.value)} style={S.select}>
              <option value="">Choose a client…</option>
              {data.groups.filter(g => g.id !== groupId).map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
            </select>
          </label>
        )}
      </div>
      {group && (
        <p style={S.scopeNote}>
          Changes below apply only to <strong>{group.name}</strong>. <Emo e="🔒" /> rows are Super Admins only and can't be given to a role.
        </p>
      )}

      {adding && group && (
        <AddRole levels={data.levels} group={group} getToken={getToken} notify={notify}
          onAdded={() => { setAdding(false); load(); }} />
      )}

      {group && (
        <div style={S.tableWrap}>
          <table style={S.table}>
            <thead>
              <tr>
                <th style={{ ...roleTh, ...permCol, ...S.permHead }}>Permission</th>
                {shownRoles.map(r => (
                  <th key={r.id} style={roleTh}>
                    {renaming?.id === r.id ? (
                      <form onSubmit={saveRename} style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                        <input value={renaming.value} autoFocus maxLength={40} aria-label="Role name"
                          onChange={e => setRenaming({ id: r.id, value: e.target.value })}
                          onKeyDown={e => { if (e.key === 'Escape') setRenaming(null); }}
                          style={S.renameInput} />
                        <button type="submit" style={S.renameBtn}>Save</button>
                      </form>
                    ) : <div style={S.roleName}>{r.label}</div>}
                    <div style={S.roleMeta}>
                      {r.builtIn ? 'Built-in' : `Works like ${data.levels.find(l => l.id === r.level)?.label}`} · {r.members} {r.members === 1 ? 'person' : 'people'}
                    </div>
                    <div style={S.roleActions}>
                      {!r.builtIn && <button onClick={() => setRenaming({ id: r.id, value: r.label })} style={S.linkBtn}>Rename</button>}
                      {!r.builtIn && <button onClick={() => remove(r)} style={{ ...S.linkBtn, color: 'var(--t-b71c1c)' }}>Delete</button>}
                      {r.builtIn && r.changed && <button onClick={() => reset(r)} style={S.linkBtn}>Reset to defaults</button>}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sections.map(sec => [
                <tr key={`sec:${sec.name}`}><td colSpan={shownRoles.length + 1} style={S.section}><span style={{ position: 'sticky', left: 14 }}>{sec.name}</span></td></tr>,
                ...sec.items.map(p => (
                  <tr key={p.key} style={S.tr}>
                    <td style={{ ...S.td, ...permCol }}>
                      {p.label}
                      {p.needs && <div style={S.needs}>Works with: {p.needs.map(nameOf).join(' or ')}</div>}
                    </td>
                    {shownRoles.map(r => {
                      const v = r.permissions[p.key];
                      const busy = saving === `${r.id}:${p.key}`;
                      return (
                        <td key={r.id} style={{ ...S.td, textAlign: 'center' }}>
                          {p.type === 'locked' ? (
                            <span title="Super Admins only. This can't be given to a role." style={S.locked}><Emo e="🔒" /></span>
                          ) : p.type === 'fixed' ? (
                            // Decided by the role's level (e.g. Admins and Super Admins), can't be changed
                            <span title={p.note || "Set by the role's level. It can't be changed."} style={S.locked}>
                              {(LEVEL_RANK[r.level] || 0) >= (LEVEL_RANK[p.minLevel] || 99) ? '✔' : '—'}
                            </span>
                          ) : p.type === 'bool' ? (
                            <input type="checkbox" checked={!!v} disabled={busy}
                              aria-label={`${r.label}: ${p.label}`}
                              onChange={e => setPermission(r, p.key, e.target.checked)} style={S.check} />
                          ) : (
                            <select value={v} disabled={busy} aria-label={`${r.label}: ${p.label}`}
                              onChange={e => setPermission(r, p.key, e.target.value)} style={S.cellSelect}>
                              {p.choices.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                            </select>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                )),
              ])}
            </tbody>
          </table>
        </div>
      )}
      <p style={S.foot}>
        People always act only on people whose role sits below theirs at the same client: Managers on Users, Admins on Managers and Users.
        A custom role sits where the role it "works like" sits.
      </p>
    </div>
  );
}

function AddRole({ levels, group, getToken, notify, onAdded }) {
  const [label, setLabel] = useState('');
  const [level, setLevel] = useState('user');
  const [busy, setBusy]   = useState(false);
  const [err, setErr]     = useState('');

  async function submit(e) {
    e.preventDefault();
    setErr(''); setBusy(true);
    try {
      await createRole(group.id, { label: label.trim(), level }, getToken);
      notify({ tone: 'success', title: `Role "${label.trim()}" added to ${group.name}` });
      onAdded();
    } catch (e2) { setErr(e2.message); }
    setBusy(false);
  }

  return (
    <form onSubmit={submit} style={S.addBox}>
      <div style={S.addFields}>
        <label style={S.fieldLabel}>Role name
          <input value={label} onChange={e => setLabel(e.target.value)} required maxLength={40} autoFocus
            placeholder="e.g. Billing Viewer" style={S.input} />
        </label>
        <label style={S.fieldLabel}>Works like
          <select value={level} onChange={e => setLevel(e.target.value)} style={S.input}>
            {levels.map(l => <option key={l.id} value={l.id}>{l.label}</option>)}
          </select>
        </label>
        <button type="submit" disabled={busy} style={{ ...S.primaryBtn, alignSelf: 'flex-end' }}>{busy ? 'Adding…' : 'Add role'}</button>
      </div>
      {err && <div role="alert" style={S.error}>{err}</div>}
      <p style={S.help}>
        The new role starts with the same permissions as the role it works like; change them in the table after adding it.
        "Works like" also decides who can reset its two-factor authentication and who it can manage.
      </p>
    </form>
  );
}

const S = {
  top: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap', marginBottom: 12 },
  title: { margin: '0 0 4px', color: 'var(--t-00695c)', fontSize: 18, fontWeight: 700 },
  sub: { margin: 0, color: 'var(--t-607d8b)', fontSize: 13, lineHeight: 1.5, maxWidth: 720 },
  primaryBtn: { padding: '8px 16px', background: 'var(--c-00897b)', color: 'var(--t-ffffff)', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit' },
  groupRow: { display: 'flex', alignItems: 'flex-end', gap: 16, flexWrap: 'wrap', marginBottom: 12 },
  groupLabel: { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, fontWeight: 700, color: 'var(--t-546e7a)', textTransform: 'uppercase', letterSpacing: '0.4px' },
  select: { padding: '8px 12px', borderRadius: 8, border: '1.5px solid var(--c-cfd8dc)', fontSize: 14, fontFamily: 'inherit', minWidth: 220 },
  domains: { fontSize: 13, color: 'var(--t-607d8b)', paddingBottom: 9 },
  tableWrap: { background: 'var(--c-ffffff)', borderRadius: 12, overflow: 'auto', boxShadow: '0 1px 6px rgb(var(--c-000000-rgb) / 0.07)', border: '1px solid var(--b-e8f5e9)' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { padding: '12px 14px', background: 'var(--c-00695c)', color: 'var(--t-ffffff)', textAlign: 'center', verticalAlign: 'top', minWidth: 130 },
  permCol: { textAlign: 'left', minWidth: 260, position: 'sticky', left: 0, zIndex: 1 },
  permHead: { background: 'var(--c-00695c)', verticalAlign: 'bottom', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.5px' },
  roleName: { fontSize: 14, fontWeight: 700 },
  renameInput: { width: 120, padding: '4px 6px', borderRadius: 6, border: 'none', fontSize: 13, fontFamily: 'inherit' },
  renameBtn: { padding: '4px 8px', borderRadius: 6, border: 'none', background: 'var(--c-e0f2f1)', color: 'var(--t-00695c)', fontSize: 12, fontWeight: 700, cursor: 'pointer' },
  roleMeta: { fontSize: 11, opacity: 0.85, marginTop: 2, fontWeight: 400 },
  roleActions: { display: 'flex', gap: 8, justifyContent: 'center', marginTop: 4 },
  linkBtn: { background: 'none', border: 'none', color: 'var(--t-e0f2f1)', cursor: 'pointer', fontSize: 11, textDecoration: 'underline', padding: 0, fontFamily: 'inherit' },
  section: { padding: '10px 14px 6px', fontSize: 11, fontWeight: 800, color: 'var(--t-00695c)', textTransform: 'uppercase', letterSpacing: '0.6px', background: 'var(--c-f1f8f6)' },
  tr: { borderBottom: '1px solid var(--c-f0f0f0)' },
  td: { padding: '9px 14px', fontSize: 13, color: 'var(--t-333333)', background: 'var(--c-ffffff)' },
  needs: { fontSize: 11, color: 'var(--t-90a4ae)', marginTop: 2 },
  check: { width: 18, height: 18, cursor: 'pointer', accentColor: 'var(--c-00897b)' },
  cellSelect: { padding: '5px 8px', borderRadius: 6, border: '1.5px solid var(--c-cfd8dc)', fontSize: 12, fontFamily: 'inherit' },
  foot: { fontSize: 12, color: 'var(--t-78909c)', marginTop: 10, lineHeight: 1.5 },
  locked: { fontSize: 14, opacity: 0.6, cursor: 'help' },
  scopeNote: { margin: '0 0 12px', fontSize: 13, color: 'var(--t-37474f)', background: 'var(--c-f1f8f6)', padding: '8px 12px', borderRadius: 8 },
  addBox: { background: 'var(--c-ffffff)', border: '1px solid var(--c-e0f2f1)', borderRadius: 12, padding: '14px 18px', marginBottom: 14 },
  addFields: { display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' },
  fieldLabel: { display: 'flex', flexDirection: 'column', gap: 5, fontSize: 13, fontWeight: 600, color: 'var(--t-546e7a)', flex: '1 1 200px' },
  input: { padding: '9px 12px', borderRadius: 8, border: '1.5px solid var(--c-e0e0e0)', fontSize: 14, fontFamily: 'inherit' },
  help: { margin: '10px 0 0', fontSize: 12, color: 'var(--t-78909c)', lineHeight: 1.5 },
  error: { background: 'var(--c-fce4ec)', color: 'var(--t-b71c1c)', padding: '10px 14px', borderRadius: 8, margin: '8px 0', fontSize: 13 },
  muted: { color: 'var(--t-90a4ae)', fontSize: 14, padding: 20 },
};
